import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import Dashboard from './pages/DashboardNew';
import FoodTracker from './pages/FoodTracker';
import GroceryList from './pages/GroceryList';
import AddFoodPage from './pages/AddFoodPage';
import LoginPage from './pages/LoginPage';
import SettingsPage from './pages/SettingsPage';
import MacroCalculatorPage from './pages/MacroCalculatorPage';
import ProgressReports from './pages/ProgressReports';
import NutritionCoach from './pages/NutritionCoach';
import SavedRecipes from './pages/SavedRecipes';
import MacroSnacks from './pages/MacroSnacks';
import ContactPage from './pages/ContactPage';
import UpgradePage from './pages/UpgradePage';
import BooksPage from './pages/BooksPage';
import MacrosMadeEasySalesPage from './pages/MacrosMadeEasySalesPage';
import BookPurchaseDetailsPage from './pages/BookPurchaseDetailsPage';
import LeadMagnetPage from './pages/LeadMagnetPage';
import LandingPage from './pages/LandingPage';
import BlogPost from './pages/BlogPost';
import BlogIndex from './pages/BlogIndex';
import AdminEmail from './pages/AdminEmail';
import NotFound from './pages/NotFound';
import LegalPage from './pages/LegalPage';
import LoadingScreen from './components/UI/LoadingScreen';
import { AuthProvider, ProtectedRoute } from './context/AuthContext';
import { NutritionProvider } from './context/NutritionContext';
import DataPersister from './components/DataPersister';
import { trackMetaPageView } from './lib/metaPixel';
import { isIosApp } from './utils/appPlatform';

const NonIosRoute = ({ children }) => (
  isIosApp() ? <Navigate to="/dashboard" replace /> : children
);

const MetaRouteTracker = () => {
  const location = useLocation();
  const hasTrackedInitialRoute = useRef(false);

  useEffect(() => {
    if (!hasTrackedInitialRoute.current) {
      hasTrackedInitialRoute.current = true;
      return;
    }

    trackMetaPageView();
  }, [location.pathname, location.search, location.hash]);

  return null;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={isIosApp() ? <Navigate to="/login" replace /> : <LandingPage />} />
    <Route path="/login" element={<LoginPage />} />
    <Route path="/privacy" element={<LegalPage type="privacy" />} />
    <Route path="/terms" element={<LegalPage type="terms" />} />
    <Route
      path="/dashboard"
      element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/grocery"
      element={
        <ProtectedRoute>
          <Layout>
            <GroceryList />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/tracker"
      element={
        <ProtectedRoute>
          <Layout>
            <FoodTracker />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/add-food"
      element={
        <ProtectedRoute>
          <Layout>
            <AddFoodPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/settings"
      element={
        <ProtectedRoute>
          <Layout>
            <SettingsPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/macro-calculator"
      element={
        <ProtectedRoute>
          <Layout>
            <MacroCalculatorPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/progress-reports"
      element={
        <ProtectedRoute>
          <Layout>
            <ProgressReports />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/nutrition-coach"
      element={
        <ProtectedRoute>
          <Layout>
            <NutritionCoach />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/saved-recipes"
      element={
        <ProtectedRoute>
          <Layout>
            <SavedRecipes />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route path="/macro-snacks" element={
      <NonIosRoute>
        <ProtectedRoute>
          <Layout>
            <MacroSnacks />
          </Layout>
        </ProtectedRoute>
      </NonIosRoute>
    } />
    <Route path="/contact" element={
      <NonIosRoute>
        <ProtectedRoute>
          <Layout>
            <ContactPage />
          </Layout>
        </ProtectedRoute>
      </NonIosRoute>
    } />
    <Route path="/upgrade" element={
      <ProtectedRoute>
        <Layout>
          <UpgradePage />
        </Layout>
      </ProtectedRoute>
    } />
    <Route
      path="/admin/email"
      element={
        <ProtectedRoute>
          <Layout>
            <AdminEmail />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route path="/blog" element={<NonIosRoute><BlogIndex /></NonIosRoute>} />
    <Route path="/blog/category/:categorySlug" element={<NonIosRoute><BlogIndex /></NonIosRoute>} />
    <Route path="/books" element={<NonIosRoute><BooksPage /></NonIosRoute>} />
    <Route path="/books/macros-made-easy-for-women" element={<NonIosRoute><MacrosMadeEasySalesPage /></NonIosRoute>} />
    <Route path="/book-purchase-details" element={<NonIosRoute><BookPurchaseDetailsPage /></NonIosRoute>} />
    <Route path="/free-macro-starter-plan" element={<NonIosRoute><LeadMagnetPage /></NonIosRoute>} />
    <Route path="/home" element={<Navigate to="/" replace />} />
    {/* /:slug must be last — catches all WordPress post URLs at root level */}
    <Route path="/:slug" element={<NonIosRoute><BlogPost /></NonIosRoute>} />
    <Route path="*" element={<NotFound />} />
  </Routes>
);

function App() {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      // Simulate quick app bootstrap; replace with real init if needed
      const timer = setTimeout(() => {
        setIsReady(true);
      }, 200);
      return () => clearTimeout(timer);
    } catch (err) {
      console.error('App initialization error:', err);
      setError(err.message || 'Unknown error');
    }
  }, []);

  if (error) {
    return (
      <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
        <h1>App Error</h1>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    );
  }

  if (!isReady) {
    return <LoadingScreen />;
  }

  return (
    <AuthProvider>
      <NutritionProvider>
        <DataPersister />
        <MetaRouteTracker />
        <AppRoutes />
      </NutritionProvider>
    </AuthProvider>
  );
}

export default App;
